import React, { FormEvent, useEffect, useRef, useState } from "react";
import { ulid } from "ulid";
import PricingCandidatesRepository, { CandidateApiError } from "../../../api/request/PricingCandidates/PricingCandidates.service";
import { Candidate, Capabilities, Capitals, isCapitals, Selection, TRAITS, Trait } from "../../../api/request/PricingCandidates/types";
import { addIntent, CandidateIntent, Intents, readIntents, SelectionIntent } from "./intentStorage";
import "./candidates.scss";

type Repository = Pick<PricingCandidatesRepository, "candidate" | "selection">;
interface Props { leadId: string; capabilities: Capabilities; repository: Repository; storage?: Storage; }
const labels: Record<Trait, string> = { DEATH: "Fallecimiento", DISABILITY: "Incapacidad permanente", SERIOUS_ILLNESS: "Enfermedad grave" };
const money = (cents: number) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(cents / 100);
const capital = (euros: number) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(euros);
const initial: Intents = { version: 1, candidates: [], selections: [] };
const errorText = (error: unknown) => error instanceof CandidateApiError && error.status === 403
  ? "No podemos acceder a esta cotización. Vuelve a iniciar sesión."
  : "No se ha podido confirmar el resultado. Conservamos la referencia para consultar su estado.";
const diagnosis = (code: string | null) => {
  if (code === "INPUT_INCOMPLETE") return "Faltan datos para calcular una cotización. Contacta con nuestro equipo para completarlos.";
  if (code === "PROFILE_UNSUPPORTED") return "El cálculo de nuevas opciones todavía no está disponible para este producto.";
  if (code === "CREATION_DISABLED") return "El cálculo de nuevas opciones está temporalmente desactivado. Puedes consultar las guardadas.";
  return "Este resultado necesita revisión de nuestro equipo. Conservamos la cotización; no se ha contratado ninguna póliza.";
};

export default function CandidateUpsell({ leadId, capabilities, repository, storage = window.localStorage }: Props) {
  const [intents, setIntents] = useState<Intents>(initial);
  const [candidates, setCandidates] = useState<Record<string, Candidate>>({});
  const [selections, setSelections] = useState<Record<string, Selection>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<Record<string, boolean>>({});
  const [blocked, setBlocked] = useState<Record<string, boolean>>({});
  const [accessDenied, setAccessDenied] = useState(false);
  const requests = useRef<Record<string, number>>({});
  const [draft, setDraft] = useState<Record<Trait, string>>({ DEATH: "", DISABILITY: "", SERIOUS_ILLNESS: "" });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [storageReady, setStorageReady] = useState(false);
  const alive = useRef(true);
  const mergeCandidate = (value: Candidate) => setCandidates(previous => {
    const prior = previous[value.candidateId];
    return prior?.state === "archived" || (prior?.diagnosticCode === "REMOTE_CONFLICT" && value.state !== "archived")
      ? previous : { ...previous, [value.candidateId]: value };
  });
  const mergeSelection = (value: Selection) => {
    mergeCandidate(value.candidate);
    setSelections(previous => {
      const prior = previous[value.selectionId];
      return prior?.state === "selected" || (prior?.diagnosticCode === "REMOTE_CONFLICT" && value.state !== "selected")
        ? previous : { ...previous, [value.selectionId]: value };
    });
  };

  const refreshCandidate = async (intent: CandidateIntent, retry = false) => {
    const sequence = requests.current[intent.candidateId] = (requests.current[intent.candidateId] ?? 0) + 1;
    try {
      const result = await repository.candidate(intent.candidateId, retry ? intent.capitalsByTrait : undefined);
      if (!alive.current) return;
      if (sequence !== requests.current[intent.candidateId] && result.state !== "archived" && result.diagnosticCode !== "REMOTE_CONFLICT") return;
      // Even a valid response must still describe the option associated with this intention.
      if (TRAITS.some(trait => result.capitalsByTrait[trait] !== intent.capitalsByTrait[trait])) throw new Error("CANDIDATE_OPTIONS_MISMATCH");
      mergeCandidate(result);
      if (sequence !== requests.current[intent.candidateId]) return;
      setBlocked(previous => ({ ...previous, [intent.candidateId]: false }));
      setMissing(previous => ({ ...previous, [intent.candidateId]: false }));
      setErrors(previous => ({ ...previous, [intent.candidateId]: "" }));
    } catch (error) {
      if (sequence !== requests.current[intent.candidateId]) return;
      if (alive.current) {
        setBlocked(previous => ({ ...previous, [intent.candidateId]: true }));
        setMissing(previous => ({ ...previous, [intent.candidateId]: !retry && error instanceof CandidateApiError && error.status === 404 }));
        if (error instanceof CandidateApiError && error.status === 403) setAccessDenied(true);
      }
      if (alive.current) setErrors(previous => ({ ...previous, [intent.candidateId]: errorText(error) }));
    }
  };
  const refreshSelection = async (intent: SelectionIntent, retry = false) => {
    const sequence = requests.current[intent.selectionId] = (requests.current[intent.selectionId] ?? 0) + 1;
    try {
      const result = await repository.selection(intent.selectionId, retry ? intent.candidateId : undefined);
      if (!alive.current) return;
      if (sequence !== requests.current[intent.selectionId] && result.state !== "selected" && result.diagnosticCode !== "REMOTE_CONFLICT") return;
      if (result.candidateId !== intent.candidateId) throw new Error("CANDIDATE_SELECTION_CONFLICT");
      mergeSelection(result);
      if (sequence !== requests.current[intent.selectionId]) return;
      setBlocked(previous => ({ ...previous, [intent.selectionId]: false }));
      setMissing(previous => ({ ...previous, [intent.selectionId]: false }));
      setErrors(previous => ({ ...previous, [intent.selectionId]: "" }));
    } catch (error) {
      if (sequence !== requests.current[intent.selectionId]) return;
      if (alive.current) {
        setBlocked(previous => ({ ...previous, [intent.selectionId]: true }));
        setMissing(previous => ({ ...previous, [intent.selectionId]: !retry && error instanceof CandidateApiError && error.status === 404 }));
        if (error instanceof CandidateApiError && error.status === 403) setAccessDenied(true);
      }
      if (alive.current) setErrors(previous => ({ ...previous, [intent.selectionId]: errorText(error) }));
    }
  };

  useEffect(() => {
    alive.current = true;
    try {
      const saved = readIntents(leadId, storage);
      setIntents(saved); setStorageReady(true);
      Promise.all([...saved.candidates.map(i => refreshCandidate(i)), ...saved.selections.map(i => refreshSelection(i))])
        .finally(() => { if (alive.current) setLoaded(true); });
    } catch {
      setMessage("No podemos recuperar las referencias guardadas en este navegador. Contacta con nuestro equipo antes de crear otra cotización.");
      setLoaded(true);
    }
    return () => { alive.current = false; };
    // The parent remounts this view when lead or authenticated repository changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, repository, storage]);

  const perform = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try { await action(); } finally { busyRef.current = false; if (alive.current) setBusy(false); }
  };
  const save = (intent: CandidateIntent | SelectionIntent) => {
    try { setIntents(addIntent(leadId, storage, intent)); return true; }
    catch {
      setStorageReady(false);
      setMessage("No podemos guardar la referencia en este navegador. No hemos enviado una nueva operación.");
      return false;
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault(); setMessage("");
    if (!capabilities.creationAllowed || !storageReady || !loaded || accessDenied) return;
    const values: Record<string, number> = {};
    for (const trait of TRAITS) {
      if (draft[trait] === "") continue;
      if (!/^[1-9]\d*$/.test(draft[trait])) { setMessage("Introduce capitales en euros enteros y mayores que cero."); return; }
      values[trait] = Number(draft[trait]);
    }
    if (!isCapitals(values)) { setMessage("Introduce un capital de fallecimiento válido."); return; }
    void perform(async () => {
      const intent = { candidateId: ulid(), capitalsByTrait: values as Capitals };
      if (save(intent)) await refreshCandidate(intent, true);
    });
  };
  const select = (candidateId: string) => void perform(async () => {
    const intent = { selectionId: ulid(), candidateId };
    if (save(intent)) await refreshSelection(intent, true);
  });
  // Replaying an earlier selection receipt must not replace a later confirmed selection.
  const selected = Object.values(selections).filter(s => s.state === "selected")
    .sort((a, b) => (b.selection?.version ?? 0) - (a.selection?.version ?? 0))[0];
  const pendingSelection = intents.selections.some(i => !selections[i.selectionId] || selections[i.selectionId].state === "pending");
  const unresolvedCandidate = intents.candidates.some(i => !candidates[i.candidateId] || candidates[i.candidateId].state !== "archived");

  return <main className="candidate-upsell">
    <h1>Tus cotizaciones</h1>
    <p>Calcula distintas opciones y compara sus condiciones. Cada cotización se guarda por separado.</p>
    <p className="candidate-upsell__notice">Todavía no están disponibles para contratar.</p>
    {!loaded && <p role="status">Recuperando tus cotizaciones…</p>}
    {message && <p role="alert">{message}</p>}
    {!capabilities.creationAllowed && <p role="status">{diagnosis(capabilities.diagnosticCode)}</p>}
    <form onSubmit={submit} aria-label="Nueva cotización">
      <h2>Calcula una opción</h2>
      <div className="candidate-upsell__fields">{TRAITS.map(trait => <label key={trait}>
        {labels[trait]} (€){trait !== "DEATH" && <span> · opcional</span>}
        <input name={trait} inputMode="numeric" autoComplete="off" value={draft[trait]}
          disabled={!capabilities.creationAllowed || busy || !storageReady}
          onChange={e => setDraft(previous => ({ ...previous, [trait]: e.target.value }))} />
      </label>)}</div>
      <button type="submit" disabled={busy || !loaded || !storageReady || accessDenied || !capabilities.creationAllowed || unresolvedCandidate || pendingSelection}>
        {busy ? "Consultando…" : "Calcular y guardar opción"}
      </button>
      {(unresolvedCandidate || pendingSelection) && <p>Consulta el resultado de la operación pendiente antes de crear otra opción.</p>}
    </form>
    <section aria-label="Cotizaciones guardadas" className="candidate-upsell__options">
      {intents.candidates.map((intent, index) => {
        const c = candidates[intent.candidateId];
        const chosen = selected?.candidateId === intent.candidateId;
        return <article key={intent.candidateId} className={chosen ? "candidate-upsell__option is-selected" : "candidate-upsell__option"}
          aria-label={`Opción ${index + 1}`}>
          <h2>Opción {index + 1}{chosen && " · Seleccionada"}</h2>
          {errors[intent.candidateId] && <p role="alert">{errors[intent.candidateId]}</p>}
          {c && <>
            <dl>{TRAITS.filter(t => c.capitalsByTrait[t] !== undefined).map(t => <React.Fragment key={t}>
              <dt>{labels[t]}</dt><dd>{capital(c.capitalsByTrait[t]!)}</dd>
            </React.Fragment>)}<dt>Fecha de efecto propuesta</dt><dd>{c.effectDateOriginal.split("-").reverse().join("/")}</dd></dl>
            {c.price && <div className="candidate-upsell__price">
              <strong>{money(c.price.yearlyPriceCents)}</strong><span>Precio anual con impuestos</span>
              <details><summary>Ver calendario mensual estimado</summary><ol>
                {c.price.monthlyPricesCents.map((amount, i) => <li key={i}>{i === 0 ? "Primer pago" : `Mes ${i + 1}`}: {money(amount)}</li>)}
              </ol></details>
            </div>}
            {c.state === "archived" && c.rateStatus !== "ACCEPTED" && <p>Esta opción no está disponible para seleccionar. Su resultado se ha guardado.</p>}
            {c.nextAction === "contact_support" && <p>{diagnosis(c.diagnosticCode)}</p>}
            {c.nextAction === "poll" && !blocked[intent.candidateId] && <p role="status">El cálculo todavía está en curso.</p>}
          </>}
          <div className="candidate-upsell__actions">
            <button disabled={busy} onClick={() => void perform(() => refreshCandidate(intent))}>Consultar estado de opción {index + 1}</button>
            {!accessDenied && ((!blocked[intent.candidateId] && c?.nextAction === "retry_same_intention") || (!c && missing[intent.candidateId])) && <button disabled={busy} onClick={() => void perform(() => refreshCandidate(intent, true))}>Recuperar esta opción</button>}
            {c?.state === "archived" && c.rateStatus === "ACCEPTED" && c.price && <button
              disabled={busy || chosen || pendingSelection || unresolvedCandidate || !storageReady || accessDenied || blocked[intent.candidateId]}
              onClick={() => select(intent.candidateId)}>Seleccionar opción {index + 1}</button>}
          </div>
        </article>;
      })}
    </section>
    {intents.selections.filter(i => selections[i.selectionId]?.state !== "selected").map(intent => {
      const s = selections[intent.selectionId];
      return <section key={intent.selectionId} aria-label="Selección pendiente">
        <h2>Selección pendiente de confirmar</h2>
        {errors[intent.selectionId] && <p role="alert">{errors[intent.selectionId]}</p>}
        {s?.nextAction === "contact_support" && <p>{diagnosis(s.diagnosticCode)}</p>}
        <button disabled={busy} onClick={() => void perform(() => refreshSelection(intent))}>Consultar selección</button>
        {!accessDenied && ((!blocked[intent.selectionId] && s?.nextAction === "retry_same_intention") || (!s && missing[intent.selectionId])) && <button disabled={busy} onClick={() => void perform(() => refreshSelection(intent, true))}>Recuperar selección</button>}
      </section>;
    })}
    {selected && <p role="status" className="candidate-upsell__confirmation">Cotización seleccionada. Todavía no está disponible para contratar.</p>}
  </main>;
}
